// src/WelcomeAuthPage.js

import React, { useMemo, useRef, useState } from "react";
import api from "./api";
import { useNavigate } from "react-router-dom";
import { setUser } from "./auth";

import heroImage from "./assets/House1.jpg";
import logoImage from "./assets/house.jpg";

export default function WelcomeAuthPage() {
  const nav = useNavigate();

  const [mode, setMode] = useState("login");
  const [remember, setRemember] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const emailRef = useRef("");
  const passwordRef = useRef("");
  const confirmPasswordRef = useRef("");

  const styles = useMemo(() => getStyles(heroImage), []);

  const getEmail = () => (emailRef.current || "").trim();
  const getPassword = () => passwordRef.current || "";
  const getConfirmPassword = () => confirmPasswordRef.current || "";

  async function handleLogin(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);

    try {
      const res = await api.post("/api/auth/login", {
        email: getEmail(),
        password: getPassword(),
      });

      setUser(res.data.user);

      if (res.data?.token) {
        localStorage.setItem("token", res.data.token);
      }

      if (remember) localStorage.setItem("wopro_remember", "1");
      else localStorage.removeItem("wopro_remember");

      nav("/");
    } catch (err) {
      setMsg(err?.response?.data?.error || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleSignup(e) {
    e.preventDefault();
    setMsg("");

    const password = getPassword();
    const confirmPassword = getConfirmPassword();

    if (password.length < 6) {
      setMsg("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setMsg("Passwords do not match. Please retype the same password.");
      return;
    }

    setBusy(true);

    try {
      const res = await api.post("/api/auth/signup", {
        email: getEmail(),
        password,
      });

      if (res.data?.approved === true && res.data?.user) {
        setUser(res.data.user);

        if (res.data?.token) {
          localStorage.setItem("token", res.data.token);
        }

        nav("/");
        return;
      }

      if (res.data?.approved === false) {
        setMsg("Account created. Please verify your email, then wait for admin approval.");
        return;
      }

      setMsg(res.data?.message || "Signup submitted.");
    } catch (err) {
      setMsg(err?.response?.data?.error || "Signup failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);

    try {
      await api.post("/api/auth/forgot-password", { email: getEmail() });
      setMsg("If the email exists, a reset link was sent.");
    } catch (err) {
      setMsg("Could not send reset link.");
    } finally {
      setBusy(false);
    }
  }

  const Brand = ({ compact = false }) => (
    <div style={compact ? styles.compactBrand : styles.brandRow}>
      <img src={logoImage} alt="House Foods logo" style={compact ? styles.compactLogoImg : styles.logoImg} />
      <div>
        <div style={compact ? styles.compactBrandName : styles.brandName}>HFAmaint</div>
        <div style={compact ? styles.compactBrandTag : styles.brandTag}>
          CMMS Maintenance Platform
        </div>
      </div>
    </div>
  );

  const ModeTabs = () => (
    <div style={styles.tabsRow}>
      <button
        type="button"
        onClick={() => {
          setMode("login");
          setMsg("");
        }}
        style={{ ...styles.tabBtn, ...(mode === "login" ? styles.tabBtnActive : {}) }}
      >
        Sign in
      </button>
      <button
        type="button"
        onClick={() => {
          setMode("signup");
          setMsg("");
        }}
        style={{ ...styles.tabBtn, ...(mode === "signup" ? styles.tabBtnActive : {}) }}
      >
        Create account
      </button>
    </div>
  );

  const Field = ({ label, children, hint }) => (
    <div style={styles.field}>
      <div style={styles.labelRow}>
        <label style={styles.label}>{label}</label>
        {hint ? <span style={styles.hint}>{hint}</span> : null}
      </div>
      <div style={styles.inputWrap}>{children}</div>
    </div>
  );

  return (
    <div style={styles.page}>
      <style>{styles.css}</style>

      <div style={styles.shell} className="hfa-fade-in">
        <section style={styles.left}>
          <div style={styles.leftOverlay} />

          <div style={styles.leftTop}>
            <Brand />
          </div>

          <div style={styles.leftContent}>
            <div style={styles.badge}>House Foods America Maintenance</div>
            <h1 style={styles.heroTitle}>Smarter Maintenance Stronger Operations</h1>
            <p style={styles.heroText}>
              Manage work orders, preventive maintenance, assets, parts, and production reliability from one clean CMMS dashboard.
            </p>

            <div style={styles.heroStats}>
              <div style={styles.heroStat}>
                <strong></strong>
                <span>Work Orders</span>
              </div>
              <div style={styles.heroStat}>
                <strong></strong>
                <span>Preventive Maintenance</span>
              </div>
              <div style={styles.heroStat}>
                <strong>24/7</strong>
                <span>Plant Reliability</span>
              </div>
            </div>
          </div>
        </section>

        <section style={styles.right}>
          <div style={styles.formCard}>
            <div style={styles.formHeader}>
              <Brand compact />
              {mode !== "forgot" ? <ModeTabs /> : null}
            </div>

            {mode === "login" && (
              <form onSubmit={handleLogin} style={styles.form}>
                <div>
                  <div style={styles.formTitle}>Welcome back</div>
                  <div style={styles.formSubtitle}>Sign in to your HFAmaint dashboard</div>
                </div>

                <Field label="Company Email">
                  <input
                    className="hfa-input"
                    style={styles.input}
                    defaultValue=""
                    onInput={(e) => (emailRef.current = e.currentTarget.value)}
                    placeholder="you@house-foods.com"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </Field>

                <Field
                  label="Password"
                  hint={
                    <button
                      type="button"
                      style={styles.inlineLink}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowPass((s) => !s)}
                    >
                      {showPass ? "Hide" : "Show"}
                    </button>
                  }
                >
                  <input
                    className="hfa-input"
                    style={{ ...styles.input, paddingRight: 54 }}
                    defaultValue=""
                    onInput={(e) => (passwordRef.current = e.currentTarget.value)}
                    placeholder="Enter your password"
                    type={showPass ? "text" : "password"}
                    autoComplete="current-password"
                    required
                  />
                </Field>

                <div style={styles.rowBetween}>
                  <label style={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      style={styles.checkbox}
                    />
                    <span>Remember me</span>
                  </label>

                  <button type="button" onClick={() => (setMode("forgot"), setMsg(""))} style={styles.linkBtn}>
                    Forgot password?
                  </button>
                </div>

                <button type="submit" style={styles.primaryBtn} disabled={busy}>
                  {busy ? "Signing in..." : "Sign in"}
                </button>

                <div style={styles.bottomSwitch}>
                  <span>New to HFAmaint?</span>
                  <button type="button" style={styles.switchBtn} onClick={() => (setMode("signup"), setMsg(""))}>
                    Create an account
                  </button>
                </div>

                {msg ? <div style={styles.msg}>{msg}</div> : null}
              </form>
            )}

            {mode === "signup" && (
              <form onSubmit={handleSignup} style={styles.form}>
                <div>
                  <div style={styles.formTitle}>Create your account</div>
                  <div style={styles.formSubtitle}>Use your company email to request access</div>
                </div>

                <Field label="Company Email">
                  <input
                    className="hfa-input"
                    style={styles.input}
                    defaultValue=""
                    onInput={(e) => (emailRef.current = e.currentTarget.value)}
                    placeholder="you@house-foods.com"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </Field>

                <Field
                  label="Password"
                  hint={
                    <button
                      type="button"
                      style={styles.inlineLink}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowPass((s) => !s)}
                    >
                      {showPass ? "Hide" : "Show"}
                    </button>
                  }
                >
                  <input
                    className="hfa-input"
                    style={styles.input}
                    defaultValue=""
                    onInput={(e) => (passwordRef.current = e.currentTarget.value)}
                    placeholder="Create a password"
                    type={showPass ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />
                </Field>

                <Field label="Retype Password">
                  <input
                    className="hfa-input"
                    style={styles.input}
                    defaultValue=""
                    onInput={(e) => (confirmPasswordRef.current = e.currentTarget.value)}
                    placeholder="Retype password"
                    type={showPass ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />
                </Field>

                <button type="submit" style={styles.primaryBtn} disabled={busy}>
                  {busy ? "Creating..." : "Create account"}
                </button>

                <div style={styles.bottomSwitch}>
                  <span>Already have an account?</span>
                  <button type="button" style={styles.switchBtn} onClick={() => (setMode("login"), setMsg(""))}>
                    Sign in
                  </button>
                </div>

                {msg ? <div style={styles.msg}>{msg}</div> : null}
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={handleForgot} style={styles.form}>
                <div>
                  <div style={styles.formTitle}>Reset your password</div>
                  <div style={styles.formSubtitle}>Enter your company email to request a reset link</div>
                </div>

                <Field label="Company Email">
                  <input
                    className="hfa-input"
                    style={styles.input}
                    defaultValue=""
                    onInput={(e) => (emailRef.current = e.currentTarget.value)}
                    placeholder="you@house-foods.com"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </Field>

                <button type="submit" style={styles.primaryBtn} disabled={busy}>
                  {busy ? "Sending..." : "Send reset link"}
                </button>

                <button type="button" style={styles.secondaryBtn} onClick={() => (setMode("login"), setMsg(""))}>
                  Back to sign in
                </button>

                {msg ? <div style={styles.msg}>{msg}</div> : null}
              </form>
            )}
          </div>

          <div style={styles.footerNote}>Secure access • Admin approval required • HFA maintenance team</div>
        </section>
      </div>
    </div>
  );
}

function getStyles(heroImage) {
  const RED = "#e30613";
  const DARK = "#111827";

  const css = `
    .hfa-fade-in {
      animation: hfaFade .45s ease-out both;
    }

    @keyframes hfaFade {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .hfa-input:focus {
      outline: none;
      border-color: rgba(227, 6, 19, 0.55);
      box-shadow: 0 0 0 5px rgba(227, 6, 19, 0.10);
      background: #ffffff;
    }

    @media (max-width: 920px) {
      .hfa-shell-responsive {
        grid-template-columns: 1fr !important;
      }
    }
  `;

  return {
    css,
    page: {
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      padding: 20,
      background: "linear-gradient(135deg, #f8fafc, #eef2f7)",
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: DARK,
    },
    shell: {
      width: "min(1180px, 100%)",
      minHeight: 690,
      display: "grid",
      gridTemplateColumns: "1.12fr 0.88fr",
      borderRadius: 30,
      overflow: "hidden",
      background: "#ffffff",
      border: "1px solid rgba(15,23,42,0.08)",
      boxShadow: "0 30px 90px rgba(15,23,42,0.18)",
    },
    left: {
      position: "relative",
      padding: 34,
      color: "#ffffff",
      backgroundImage: `url(${heroImage})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      minHeight: 690,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    leftOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(90deg, rgba(15,23,42,0.88), rgba(15,23,42,0.52), rgba(15,23,42,0.12))",
      zIndex: 1,
    },
    leftTop: {
      position: "relative",
      zIndex: 2,
    },
    brandRow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 14,
      padding: 12,
      borderRadius: 20,
      background: "rgba(255,255,255,0.92)",
      color: DARK,
      boxShadow: "0 16px 40px rgba(0,0,0,0.18)",
    },
    logoImg: {
      width: 58,
      height: 58,
      borderRadius: 16,
      objectFit: "contain",
      background: "#ffffff",
      padding: 4,
    },
    brandName: {
      fontSize: 26,
      fontWeight: 900,
      letterSpacing: "-0.04em",
      color: DARK,
    },
    brandTag: {
      marginTop: 2,
      fontSize: 12,
      color: "#64748b",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
    leftContent: {
      position: "relative",
      zIndex: 2,
      maxWidth: 570,
    },
    badge: {
      display: "inline-flex",
      padding: "8px 12px",
      borderRadius: 999,
      background: "rgba(227,6,19,0.92)",
      color: "#ffffff",
      fontSize: 12,
      fontWeight: 900,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      marginBottom: 16,
    },
    heroTitle: {
      margin: 0,
      fontSize: 46,
      lineHeight: 1.05,
      fontWeight: 950,
      letterSpacing: "-0.06em",
      textShadow: "0 14px 35px rgba(0,0,0,0.28)",
    },
    heroText: {
      margin: "16px 0 0",
      fontSize: 16,
      lineHeight: 1.7,
      color: "rgba(255,255,255,0.88)",
      maxWidth: 520,
    },
    heroStats: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 12,
      marginTop: 24,
    },
    heroStat: {
      padding: 14,
      borderRadius: 18,
      background: "rgba(255,255,255,0.13)",
      border: "1px solid rgba(255,255,255,0.22)",
      backdropFilter: "blur(8px)",
    },
    compactBrand: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    compactLogoImg: {
      width: 48,
      height: 48,
      borderRadius: 14,
      objectFit: "contain",
      background: "#ffffff",
      border: "1px solid rgba(15,23,42,0.08)",
      padding: 4,
    },
    compactBrandName: {
      fontSize: 24,
      fontWeight: 950,
      letterSpacing: "-0.05em",
      color: DARK,
    },
    compactBrandTag: {
      fontSize: 12,
      color: "#64748b",
      fontWeight: 700,
    },
    right: {
      padding: 34,
      background:
        "radial-gradient(circle at top right, rgba(227,6,19,0.08), transparent 35%), #ffffff",
      display: "grid",
      alignContent: "center",
      justifyItems: "center",
    },
    formCard: {
      width: "min(430px, 100%)",
      borderRadius: 26,
      padding: 24,
      background: "rgba(255,255,255,0.92)",
      border: "1px solid rgba(15,23,42,0.08)",
      boxShadow: "0 22px 60px rgba(15,23,42,0.10)",
    },
    formHeader: {
      display: "grid",
      gap: 18,
      marginBottom: 18,
    },
    tabsRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      padding: 5,
      borderRadius: 16,
      background: "#f1f5f9",
    },
    tabBtn: {
      height: 42,
      borderRadius: 13,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      fontWeight: 900,
      color: "#64748b",
    },
    tabBtnActive: {
      background: "#ffffff",
      color: DARK,
      boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
    },
    form: {
      display: "grid",
      gap: 15,
    },
    formTitle: {
      fontSize: 25,
      fontWeight: 950,
      letterSpacing: "-0.05em",
      color: DARK,
    },
    formSubtitle: {
      marginTop: 4,
      fontSize: 14,
      color: "#64748b",
      lineHeight: 1.5,
    },
    field: {
      display: "grid",
      gap: 7,
    },
    labelRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    label: {
      fontSize: 13,
      fontWeight: 850,
      color: "#334155",
    },
    hint: {
      fontSize: 12,
      color: "#64748b",
    },
    inputWrap: {
      position: "relative",
    },
    input: {
      width: "100%",
      height: 48,
      borderRadius: 15,
      border: "1px solid #dbe3ee",
      background: "#f8fafc",
      padding: "0 15px",
      color: DARK,
      fontSize: 14,
      transition: "all .18s ease",
    },
    rowBetween: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
    },
    checkboxRow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      color: "#475569",
      fontSize: 13,
      cursor: "pointer",
    },
    checkbox: {
      width: 16,
      height: 16,
      accentColor: RED,
    },
    linkBtn: {
      border: "none",
      background: "transparent",
      color: RED,
      fontWeight: 850,
      cursor: "pointer",
    },
    inlineLink: {
      border: "none",
      background: "transparent",
      color: RED,
      fontWeight: 850,
      cursor: "pointer",
    },
    primaryBtn: {
      height: 48,
      borderRadius: 15,
      border: "none",
      background: `linear-gradient(135deg, ${RED}, #ff4d57)`,
      color: "#ffffff",
      fontWeight: 950,
      cursor: "pointer",
      boxShadow: "0 16px 35px rgba(227,6,19,0.24)",
    },
    secondaryBtn: {
      height: 46,
      borderRadius: 15,
      border: "1px solid #dbe3ee",
      background: "#ffffff",
      color: DARK,
      fontWeight: 900,
      cursor: "pointer",
    },
    bottomSwitch: {
      display: "flex",
      justifyContent: "center",
      gap: 8,
      fontSize: 14,
      color: "#64748b",
    },
    switchBtn: {
      border: "none",
      background: "transparent",
      color: RED,
      fontWeight: 950,
      cursor: "pointer",
    },
    msg: {
      padding: "11px 13px",
      borderRadius: 15,
      background: "rgba(227,6,19,0.08)",
      border: "1px solid rgba(227,6,19,0.16)",
      color: "#7f1d1d",
      fontSize: 13.5,
      lineHeight: 1.45,
    },
    footerNote: {
      marginTop: 16,
      fontSize: 12.5,
      color: "#64748b",
      textAlign: "center",
    },
  };
}